/*
 * @format
 *
 * A durable record of the requestIds THIS client minted for control/schedule
 * commands that may still be in flight server-side.
 *
 * The pending-* slices (pendingSetpoint/Mode/HolidayMode/Schedule) are
 * ephemeral: they self-expire on the app's short client-side command timeout,
 * flip to 'done' on completion, or are debounce-cleared — all on a much shorter
 * timescale than the server's desired-state goal and its resolved[] outcomes
 * (a slow/offline device can take minutes to resolve, and an un-commandable
 * goal lingers until a prune). That gap leaves a window where the local entry
 * is gone but the server still holds our goal, so own-goal recognition can no
 * longer be driven off the pending slices alone. This slice bridges it:
 * isOwnPendingRequestId consults it so applyResolvedOutcomes can recognise a
 * resolved[] terminal as our own and clear the matching local pending on a
 * failed/superseded outcome (see actions.js — the sole consumer).
 *
 * It is a { [requestId]: homeId } map keyed by requestId (the recognition key).
 * The homeId value is retained per entry but no longer consulted — it was used
 * by a foreground heal (a bootstrap GC of stranded ids) that has since been
 * removed, so recognition today is purely key-presence.
 *  - ADD  on the command-start actions that mint a requestId (the same start
 *    types the pending-* slices consume); each carries the requestId and homeId.
 *  - REMOVE a requestId as soon as the server reports it terminal in a live
 *    push's resolved[] (any outcome — success/failed/superseded), or when the
 *    live supersede clear (PENDING_CHANGE_SUPERSEDED, dispatched by
 *    applyResolvedOutcomes) retires a finished request.
 *  - CLEAR on logout so one user's in-flight ids never leak into another
 *    user's session on a shared device (mirrors the sibling slices).
 *
 * An id whose terminal outcome was MISSED (the originator was disconnected when
 * the single live push carrying resolved[] was projected — bootstraps hard-code
 * resolved:[]) is never reclaimed by a bootstrap. This slice deliberately does
 * NOT garbage-collect on bootstrap goal-absence: a stale/lagging snapshot read
 * would evict an id whose goal the LWW-protected desiredState slice still holds,
 * desyncing own-goal recognition. Such an orphaned id simply lingers until a
 * later live resolved[]/supersede or logout clears it; the map grows by one per
 * command, so the unbounded-growth risk is negligible.
 */

import * as actionTypes from '../action-types';

export const initialState = {};

export default (state = initialState, action) => {
  switch (action.type) {
    // ADD — a control/schedule command this client just started. These are the
    // same start types the pending-* slices consume; each carries the minted
    // requestId and the homeId it targets (stored as the value — retained but no
    // longer consulted; see the header). Idempotent: repeated starts for one
    // requestId (setpoint+mode dispatched together, plus-hours, cancel-override,
    // cancel-holiday) and a duplicate id leave the reference unchanged.
    case actionTypes.SET_SETPOINT_STARTED:
    case actionTypes.SET_MODE_STARTED:
    case actionTypes.SET_HOLIDAY_MODE_STARTED:
    case actionTypes.SET_SCHEDULE_STARTED: {
      const { requestId, homeId } = action;

      if (!requestId || state[requestId]) {
        return state;
      }

      return { ...state, [requestId]: homeId };
    }

    // REMOVE — drop every requestId the server has just reported terminal in
    // this push's resolved[]. Recognition of these very outcomes as our own
    // happens before this reducer runs (see applyResolvedOutcomes ordering in
    // webSocketMessageReceived), so dropping them here is safe.
    case actionTypes.WEBSOCKET_DESIRED_STATE_RECEIVED: {
      const resolved = action.data && action.data.resolved;

      if (!Array.isArray(resolved) || resolved.length === 0) {
        return state;
      }

      const terminalIds = resolved
        .map(outcome => outcome && outcome.clientRequestId)
        .filter(id => id && state[id]);

      if (terminalIds.length === 0) {
        return state;
      }

      const next = { ...state };
      terminalIds.forEach(id => delete next[id]);
      return next;
    }

    // RETIRE — a request the live supersede path has reported finished.
    // PENDING_CHANGE_SUPERSEDED clears its pending entry and any stale error in
    // the pending-* slices; drop the durable id on the same signal. Idempotent:
    // a requestId already gone (e.g. also dropped via a resolved[] terminal in
    // the same batch) leaves the reference unchanged.
    case actionTypes.PENDING_CHANGE_SUPERSEDED: {
      const requestId =
        action.requestId || (action.data && action.data.requestId);

      if (!requestId || !state[requestId]) {
        return state;
      }

      const next = { ...state };
      delete next[requestId];
      return next;
    }

    // CLEAR — reset on logout (mirrors the sibling slices).
    case actionTypes.LOGOUT_COMPLETED:
      return initialState;

    default:
      return state;
  }
};
